import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import MonthlyGoal from "./components/MonthlyGoal";
import CalendarHeader from "./components/CalendarHeader";
import WeekStrip from "./components/WeekStrip";
import CalendarControls from "./components/CalendarControls";
import CalendarView from "./components/CalendarView";
import DailyTasksView from "./components/DailyTasksView";
import QuadrantView from "./components/QuadrantView";
import DailyReportView from "./components/DailyReportView";
import AppointmentModal from "./components/AppointmentModal";
import GoalModal from "./components/GoalModal";
import { useAppointments } from "./hooks/useAppointments";
import { 
  pad, 
  startOfDay, 
  addDays, 
  sameDay, 
  KPI_KEYS, 
  defaultKpis 
} from "./utils/calendarUtils";
import "./calendar.css";
import { useTasks } from "./hooks/useTasks";
import { db } from "../../firebase";
import { doc, updateDoc, collection, query, where, getDocs, setDoc } from "firebase/firestore";


export default function CalendarPage() {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState("day");
  const [kpis, setKpis] = useState({ ...defaultKpis });
  const [originalKpis, setOriginalKpis] = useState({ ...defaultKpis });
  const [goalModalOpen, setGoalModalOpen] = useState(false);
  const [modalValues, setModalValues] = useState({ ...defaultKpis });
  const [calFilter, setCalFilter] = useState("My Calendar");
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);

  const { appointments, loading, createAppointment, refreshAppointments, toggleAppointmentCompletion, deleteAppointment } = useAppointments(user);
  const { 
  tasks, 
  tasksLoading, 
  createTask, 
  toggleTaskCompletion, 
  deleteTask 
} = useTasks(user);

  // Load goals for current month on component mount or when user/date changes
  useEffect(() => {
    if (user) {
      loadMonthlyGoals(selectedDate);
    }
  }, [user, selectedDate]);

  // Get current month and year key
  const getMonthYearKey = (date) => {
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    return `${year}-${month}`;
  };

  // Map display names to Firestore-safe field names
  const toFirestoreKey = (key) => {
    if (key === "PR/TR") return "PR_TR";
    if (key === "PP/TP") return "PP_TP";
    return key;
  };

  const fromFirestoreKey = (key) => {
    if (key === "PR_TR") return "PR/TR";
    if (key === "PP_TP") return "PP/TP";
    return key;
  };

  // Load monthly goals from Firestore
  const loadMonthlyGoals = async (date) => {
    try {
      const monthYear = getMonthYearKey(date);
      const goalsRef = collection(db, 'monthlyGoals');
      const q = query(
        goalsRef,
        where('userId', '==', user.uid),
        where('monthYear', '==', monthYear)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const goalsDoc = querySnapshot.docs[0];
        const data = goalsDoc.data();
        
        // Extract KPI values (excluding metadata fields)
        const kpiValues = {};
        KPI_KEYS.forEach(key => {
          const firestoreKey = toFirestoreKey(key);
          // For PR/TR and PP/TP, ensure they're in "0/0" format
          if (key === "PR/TR" || key === "PP/TP") {
            const value = data[firestoreKey];
            kpiValues[key] = (typeof value === 'string' && value.includes('/')) ? value : "0/0";
          } else {
            kpiValues[key] = data[firestoreKey] || 0;
          }
        });
        
        setOriginalKpis(kpiValues);
        setKpis(kpiValues);
      } else {
        // No goals set for this month yet
        setOriginalKpis({ ...defaultKpis });
        setKpis({ ...defaultKpis });
      }
    } catch (error) {
      console.error('Error loading goals:', error);
    }
  };

  // Save goals to Firestore
  const saveGoalsToFirestore = async (goalsData) => {
    try {
      const monthYear = getMonthYearKey(selectedDate);
      const goalsRef = collection(db, 'monthlyGoals');
      
      // Check if goals already exist for this month
      const q = query(
        goalsRef,
        where('userId', '==', user.uid),
        where('monthYear', '==', monthYear)
      );
      
      const querySnapshot = await getDocs(q);
      
      // Convert display keys to Firestore-safe keys
      const firestoreData = {};
      Object.keys(goalsData).forEach(key => {
        const firestoreKey = toFirestoreKey(key);
        firestoreData[firestoreKey] = goalsData[key];
      });
      
      const goalDoc = {
        userId: user.uid,
        monthYear: monthYear,
        month: selectedDate.getMonth() + 1,
        year: selectedDate.getFullYear(),
        ...firestoreData,
        updatedAt: new Date(),
      };
      
      if (!querySnapshot.empty) {
        // Update existing document
        const docId = querySnapshot.docs[0].id;
        await updateDoc(doc(db, 'monthlyGoals', docId), goalDoc);
      } else {
        // Create new document
        const docId = `${user.uid}_${monthYear}`;
        await setDoc(doc(db, 'monthlyGoals', docId), goalDoc);
      }
      
      // Update local state
      setOriginalKpis(goalsData);
      setKpis(goalsData);
      
      console.log('Goals saved successfully');
    } catch (error) {
      console.error('Error saving goals:', error);
      alert('Failed to save goals: ' + error.message);
    }
  };

  /* Week strip range */
  const weekStart = useMemo(() => {
    const d = new Date(selectedDate);
    const day = d.getDay();
    return addDays(d, -day);
  }, [selectedDate]);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const onPrevDay = () => setSelectedDate((d) => addDays(d, -1));
  const onNextDay = () => setSelectedDate((d) => addDays(d, 1));
  const onToday = () => setSelectedDate(new Date());

  const openGoals = () => {
    setModalValues({ ...kpis });
    setGoalModalOpen(true);
  };

  const saveGoals = async (e) => {
    e.preventDefault();
    await saveGoalsToFirestore(modalValues);
    setGoalModalOpen(false);
  };

  const handleTimeSlotDoubleClick = (time, slotDate) => {
    if (!user) {
      alert("Please log in to create appointments");
      return;
    }
    if (slotDate) {
      setSelectedDate(new Date(slotDate));
    }
    setEditingAppointment(null);
    setSelectedTimeSlot(time);
    setAppointmentModalOpen(true);
  };

  const handleAppointmentClick = (appointment) => {
    setEditingAppointment(appointment);
    setSelectedTimeSlot(null);
    setAppointmentModalOpen(true);
  };

  const handleAppointmentSubmit = async (appointmentData) => {
    try {
      console.log('Submitting appointment data:', appointmentData);
      await createAppointment(appointmentData);
      setAppointmentModalOpen(false);
      setEditingAppointment(null);
      setSelectedTimeSlot(null);
      
      // Force a refresh to ensure data is consistent
      await refreshAppointments();
    } catch (error) {
      console.error('Failed to create appointment:', error);
      alert('Failed to create appointment: ' + error.message);
    }
  };

  const handleAppointmentDelete = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) {
      return;
    }
    
    try {
      await deleteAppointment(appointmentId);
      setAppointmentModalOpen(false);
      setEditingAppointment(null);
      await refreshAppointments();
    } catch (error) {
      console.error('Failed to delete appointment:', error);
      alert('Failed to delete appointment: ' + error.message);
    }
  };



  // Render the main content based on selected filter
  const renderMainContent = () => {
    switch (calFilter) {
      case "My Daily Tasks":
        return (
          <DailyTasksView 
            selectedDate={selectedDate}
            view={view}
            setView={setView}
            appointments={appointments}
            onAddNewEvent={() => {
              setEditingAppointment(null);
              setSelectedTimeSlot("9:00AM");
              setAppointmentModalOpen(true);
            }}
            onAppointmentClick={handleAppointmentClick}
            onToggleTaskCompletion={toggleAppointmentCompletion}
          />
        );
      case "My Quadrants":
        return (
          <QuadrantView
            selectedDate={selectedDate}
            appointments={appointments}
            onAppointmentClick={handleAppointmentClick}
          />
        );
      case "My Daily Report":
        return (
          <DailyReportView
            selectedDate={selectedDate}
            appointments={appointments}
          />
        );
      case "My Calendar":
      default:
        return (
          <CalendarView
            selectedDate={selectedDate}
            appointments={appointments}
            view={view}
            onTimeSlotDoubleClick={handleTimeSlotDoubleClick}
            onAppointmentClick={handleAppointmentClick}
          />
        );
    }
  };

  return (
    <div className="calendar-container">
      <MonthlyGoal 
        kpis={kpis} 
        onEditGoals={openGoals}
      />

      {/* CalendarHeader removed; header content moved into CalendarControls */}
      <CalendarControls 
        calFilter={calFilter}
        setCalFilter={setCalFilter}
        view={view}
        setView={setView}
        showViewSelector={calFilter === "My Calendar"}
        selectedDate={selectedDate}
        onPrevDay={onPrevDay}
        onNextDay={onNextDay}
        onToday={onToday}
      />
      <WeekStrip 
        weekDays={weekDays}
        selectedDate={selectedDate}
        onDateSelect={setSelectedDate}
      />
      {renderMainContent()}

      {/* Modals */}
      <AppointmentModal
        isOpen={appointmentModalOpen}
        onClose={() => {
          setAppointmentModalOpen(false);
          setEditingAppointment(null);
          setSelectedTimeSlot(null);
        }}
        selectedDate={selectedDate}
        selectedTimeSlot={selectedTimeSlot}
        onSubmit={handleAppointmentSubmit}
        onDelete={handleAppointmentDelete}
        editingAppointment={editingAppointment}
        currentUserId={user?.uid}
      />

      <GoalModal
        isOpen={goalModalOpen}
        onClose={() => setGoalModalOpen(false)}
        modalValues={modalValues}
        setModalValues={setModalValues}
        onSubmit={saveGoals}
        originalKpis={originalKpis}
        selectedDate={selectedDate}
      />

      {loading && (
        <div className="loading">Loading appointments...</div>
      )}
    </div>
  );
}